import { BellOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { App, Badge, Empty, List, Popover, Spin, Typography } from 'antd';
import { createStyles, keyframes } from 'antd-style';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getNotificationList,
  getNotificationUnreadCount,
  markNotificationRead,
} from '@/services/sys-notification/api';
import NoticeListModal from './NoticeListModal';
import {
  initNoticeAudio,
  playBeep,
  requestNotifyPermission,
  showDesktopNotification,
} from './noticeAlert';
import { useNoticeWS } from './use-notice-ws';
import type { NoticePush } from './use-notice-ws';
import { formatDateTime } from '@/utils/date';

// WebSocket 断开期间的兜底轮询间隔
const FALLBACK_POLL_MS = 60000;

const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  15%, 45%, 75% { transform: rotate(-14deg); }
  30%, 60%, 90% { transform: rotate(14deg); }
`;

const useStyles = createStyles(({ token }) => ({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '100%',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 16,
    color: token.colorTextSecondary,
    borderRadius: token.borderRadius,
    transition: 'all 0.2s',
    '&:hover': {
      color: token.colorPrimary,
      backgroundColor: token.colorBgTextHover,
    },
  },
  // overscrollBehavior: contain —— 列表滚到底后不把滚动量传给页面（避免滚动穿透）
  panel: {
    width: 360,
    maxHeight: 420,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  },
  item: { cursor: 'pointer' },
  unread: { fontWeight: 600 },
  ringing: {
    animation: `${shake} 0.5s ease-in-out 3`,
    transformOrigin: 'top center',
  },
}));

const NoticeBell: React.FC = () => {
  const { styles } = useStyles();
  const { notification } = App.useApp();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [items, setItems] = useState<SysNotificationAPI.NotificationItem[]>([]);
  const prevUnreadRef = useRef(0);
  const firstLoadRef = useRef(true);
  const permissionAskedRef = useRef(false);
  // 浮层通知独立 key 的自增序号（推送消息无唯一 ID，refId 可能重复或缺省）
  const noticeSeqRef = useRef(0);

  const triggerAlerts = useCallback(async (delta: number, info?: NoticePush) => {
    let title = info?.title || '新通知';
    let body = info?.content || `您有 ${delta} 条新通知`;
    let refId: number | undefined = info?.refId;
    let refType: string | undefined = info?.refType;
    if (!info) {
      try {
        const res = await getNotificationList({ current: 1, pageSize: 1 });
        const top = res?.data?.[0];
        if (top) {
          title = top.title;
          body = delta > 1 ? `${top.content}（等 ${delta} 条）` : top.content;
          refId = top.refId;
          refType = top.refType;
        }
      } catch {
        // 文案退化为通用提示
      }
    }

    const goDetail = () => {
      if (refType === 'sign_publish' && refId) {
        history.push(`/sign-manage/published?id=${refId}`);
      }
    };

    setRinging(true);
    setTimeout(() => setRinging(false), 1600);

    playBeep();

    if (document.visibilityState === 'visible') {
      noticeSeqRef.current += 1;
      const key = `notice-new-${noticeSeqRef.current}`;
      notification.open({
        key,
        message: title,
        description: body,
        icon: <BellOutlined style={{ color: '#1677ff' }} />,
        duration: 6,
        style: { cursor: 'pointer' },
        onClick: () => {
          goDetail();
          notification.destroy(key);
        },
      });
    } else {
      showDesktopNotification(title, body, goDetail);
    }
  }, [notification]);

  // alert=true 时（WS 断开的兜底轮询），未读数增长会触发提醒；
  // WS 在线时提醒由推送消息触发，这里只静默同步计数
  const syncUnread = useCallback(
    async (alert = false) => {
      const res = await getNotificationUnreadCount();
      if (!res?.success) return;
      const count = res.data ?? 0;
      setUnread(count);
      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        prevUnreadRef.current = count;
        return;
      }
      if (alert && count > prevUnreadRef.current) {
        triggerAlerts(count - prevUnreadRef.current);
      }
      prevUnreadRef.current = count;
    },
    [triggerAlerts],
  );

  const { connected } = useNoticeWS(
    useCallback(
      (msg: NoticePush) => {
        triggerAlerts(1, msg);
        syncUnread();
      },
      [triggerAlerts, syncUnread],
    ),
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotificationList({ current: 1, pageSize: 20 });
      setItems(res?.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    syncUnread();
    // 首个用户手势激活音频上下文（绕过浏览器自动播放限制）
    document.addEventListener('click', initNoticeAudio, { once: true });
    return () => {
      document.removeEventListener('click', initNoticeAudio);
    };
  }, [syncUnread]);

  // WS 断开时降级为兜底轮询；恢复连接后立即刷新一次
  useEffect(() => {
    if (connected) {
      syncUnread();
      return;
    }
    const timer = setInterval(() => syncUnread(true), FALLBACK_POLL_MS);
    return () => clearInterval(timer);
  }, [connected, syncUnread]);

  // 回到前台时刷新未读数
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncUnread();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [syncUnread]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      fetchList();
      // 首次打开铃铛时请求桌面通知授权（用户手势内，合规）
      if (!permissionAskedRef.current) {
        permissionAskedRef.current = true;
        requestNotifyPermission();
      }
    }
  };

  const handleClickItem = async (it: SysNotificationAPI.NotificationItem) => {
    if (!it.isRead) {
      const res = await markNotificationRead({ ids: [it.id] });
      if (res?.success) {
        setItems((prev) =>
          prev.map((x) => (x.id === it.id ? { ...x, isRead: true } : x)),
        );
        syncUnread();
      }
    }
    if (it.refType === 'sign_publish' && it.refId) {
      setOpen(false);
      history.push(`/sign-manage/published?id=${it.refId}`);
    }
  };

  const handleReadAll = async () => {
    const res = await markNotificationRead({ ids: [] });
    if (res?.success) {
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
      syncUnread();
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    setModalOpen(true);
  };

  const content = (
    <div className={styles.panel}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Typography.Link onClick={handleViewAll}>查看全部</Typography.Link>
        <Typography.Link onClick={handleReadAll}>全部已读</Typography.Link>
      </div>
      <Spin spinning={loading}>
        {items.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无消息" />
        ) : (
          <List
            dataSource={items}
            renderItem={(it) => (
              <List.Item
                className={styles.item}
                onClick={() => handleClickItem(it)}
              >
                <List.Item.Meta
                  title={
                    <span className={it.isRead ? undefined : styles.unread}>
                      {it.title}
                    </span>
                  }
                  description={
                    <>
                      <div>{it.content}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {formatDateTime(it.createTime)}
                      </div>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
      >
        <span className={styles.trigger}>
          <Badge count={unread} size="small" offset={[2, -2]}>
            <BellOutlined className={ringing ? styles.ringing : undefined} />
          </Badge>
        </span>
      </Popover>
      <NoticeListModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onUnreadChange={syncUnread}
      />
    </>
  );
};

export default NoticeBell;
